import { useState, useRef, useEffect } from "react";
import { Pencil, Trash2, Flag, ExternalLink } from "lucide-react";

import { formatDateTime } from "@/lib/format";
import { textLimit, contentPermissions, savedComment, removed, writeOutcome, postHref, UNCLEAR_TEXT, UNCLEAR_REMOVE_TEXT } from "@/lib/community";
import { safeReturnPath } from "@/lib/returnPath";
import { mutate } from "../actions";
import { ConfirmDialog, ErrorNote } from "../components/kit";
import { V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3TextArea, V3_SMALL, V3_GHOST } from "../components/v3app";

export const smallBtn = V3_SMALL;
export const RESEND_LABEL = "Stand geprüft – erneut senden freigeben";

export function useGuard() {
  const alive = useRef(true);
  const flight = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // Runs one request at a time and ignores results after unmount.
  return async (fn) => {
    if (flight.current) return undefined;
    flight.current = true;
    try { const r = await fn(); return alive.current ? r : undefined; } finally { flight.current = false; }
  };
}

// Unclear result: no retry, no guess. GET-only look in a new tab; sending stays locked until the member unlocks it.
export function UnclearNote({ testid, text, checkTo, checkLabel, unlockLabel = RESEND_LABEL, onUnlock }) {
  const after = safeReturnPath(checkTo) ? "danach führt dich die App genau zu diesem Ziel und prüft dort die Rechte des angemeldeten Kontos" : "danach öffnest du dort die Community bzw. den Beitrag selbst";
  return (
    <div role="alert" data-testid={testid} className="min-w-0 space-y-2 break-words rounded-[12px] bg-v3-sand px-[18px] py-[14px] text-[14px] text-v3-ink hyphens-auto [overflow-wrap:anywhere]">
      <p className="font-bold text-v3-amber" data-testid={`${testid}-text`}>{text}</p>
      <a href={checkTo} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-check`}
        className="inline-flex min-h-[44px] max-w-full items-center gap-1.5 rounded-[6px] font-bold text-v3-magenta underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-v3-magenta">
        <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" /> <span className="min-w-0 break-words">{checkLabel} (öffnet in neuem Tab)</span>
      </a>
      <p data-testid={`${testid}-hint`} className="text-v3-muted">Nachsehen ändert nichts und ist kein Beweis, ob gespeichert wurde. Im neuen Tab meldest du dich erneut an, weil die Anmeldung nur in diesem Tab gilt; {after}. Bis du den Stand bestätigst, bleibt Senden hier gesperrt.</p>
      <button type="button" onClick={onUnlock} data-testid={`${testid}-unlock`} className={`${V3_SMALL} h-auto py-2 text-left`}>{unlockLabel}</button>
    </div>
  );
}

// Edit own text; saved only when the server returns the changed item. onSave returns null or an outcome.
export function EditBox({ id, label, max, initial, onSave, onCancel, note, checkTo, saveLabel = "Änderung speichern" }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState("");
  const [unclear, setUnclear] = useState(false);
  const [busy, setBusy] = useState(false);
  const guard = useGuard();
  const limit = textLimit(value, max);
  const save = async () => {
    if (unclear) return;
    if (limit.error) { setError(limit.error); return; }
    setBusy(true); setError("");
    const o = await guard(() => onSave(limit.value));
    if (o === undefined) return;
    setBusy(false);
    if (o?.state === "unclear") setUnclear(true);
    else if (o) setError(o.error);
  };
  return (
    <div data-testid={`${id}-edit`}>
      <V3TextArea id={`${id}-edit-text`} label={label} max={max} value={value} onChange={(e) => setValue(e.target.value)} error={value && limit.length > max ? limit.error : ""} />
      <DetailRow label="Status" value={value.trim() === initial.trim() ? "Keine Änderung" : "Änderung noch nicht gespeichert"} testid={`${id}-edit-status`} />
      {note && <p className="mt-[12px] text-[14px] text-v3-muted">{note}</p>}
      {error && <div className="mt-[12px]"><ErrorNote testid={`${id}-edit-error`}>{error}</ErrorNote></div>}
      {unclear && <div className="mt-[12px]"><UnclearNote testid={`${id}-edit-unclear`} text={UNCLEAR_TEXT} checkTo={checkTo} checkLabel="Beitrag prüfen" onUnlock={() => setUnclear(false)} /></div>}
      <V3Actions>
        <button type="button" onClick={save} disabled={busy || unclear} data-testid={`${id}-edit-save`} className={V3_PRIMARY}>{busy ? "Wird gespeichert …" : saveLabel}</button>
        <button type="button" onClick={onCancel} disabled={busy} data-testid={`${id}-edit-cancel`} className={V3_SECONDARY}>Abbrechen</button>
      </V3Actions>
    </div>
  );
}

// Removal needs an explicit acknowledgement; the dialog stays open on failure and stays locked when unclear.
export function RemoveDialog({ open, onOpenChange, testid, title, impact, onRemove, checkTo }) {
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unclear, setUnclear] = useState(false);
  const guard = useGuard();
  const close = (o) => { if (!o && !busy) { setAck(false); setError(""); onOpenChange(false); } };
  const confirm = async () => {
    if (!ack || unclear) return;
    setBusy(true); setError("");
    const o = await guard(onRemove);
    if (o === undefined) return;
    setBusy(false);
    if (o?.state === "unclear") setUnclear(true);
    else if (o) setError(o.error);
  };
  return (
    <ConfirmDialog open={open} onOpenChange={close} testid={testid} busy={busy || !ack || unclear} title={title} confirmLabel="Endgültig entfernen" cancelLabel="Behalten" onConfirm={confirm} description={impact}>
      <label className="flex min-h-[44px] items-start gap-3 rounded-[10px] border border-red-700/30 bg-red-50 px-3 py-2">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid={`${testid}-ack`} className="mt-0.5 h-5 w-5 shrink-0 accent-[#B91C1C]" />
        <span className="min-w-0 text-sm text-red-900">Ich habe verstanden, dass das nicht rückgängig gemacht werden kann.</span>
      </label>
      {error && <ErrorNote testid={`${testid}-error`}>{error}</ErrorNote>}
      {unclear && <UnclearNote testid={`${testid}-unclear`} text={UNCLEAR_REMOVE_TEXT} checkTo={checkTo} checkLabel="Beitrag prüfen" unlockLabel="Stand geprüft – Entfernen wieder freigeben" onUnlock={() => setUnclear(false)} />}
    </ConfirmDialog>
  );
}

// M46 – removal as its own confirmation view: the impact is listed, then one deliberate click.
export function RemovePanel({ rows, onRemove, onKeep, checkTo, testid = "post-remove-panel" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unclear, setUnclear] = useState(false);
  const guard = useGuard();
  const confirm = async () => {
    if (busy || unclear) return;
    setBusy(true); setError("");
    const o = await guard(onRemove);
    if (o === undefined) return;
    setBusy(false);
    if (o?.state === "unclear") setUnclear(true);
    else if (o) setError(o.error);
  };
  return (
    <div data-testid={testid}>
      <StatusBanner tone="notice" title="Deine Bestätigung ist erforderlich" text="Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." testid={`${testid}-banner`} />
      <DetailList>{rows.map(([label, value, key]) => <DetailRow key={key} label={label} value={value} testid={`${testid}-${key}`} />)}</DetailList>
      {error && <div className="mt-[17px]"><ErrorNote testid={`${testid}-error`}>{error}</ErrorNote></div>}
      {unclear && <div className="mt-[17px]"><UnclearNote testid={`${testid}-unclear`} text={UNCLEAR_REMOVE_TEXT} checkTo={checkTo} checkLabel="Beitrag prüfen" unlockLabel="Stand geprüft – Entfernen wieder freigeben" onUnlock={() => setUnclear(false)} /></div>}
      <V3Actions>
        <button type="button" onClick={confirm} disabled={busy || unclear} data-testid={`${testid}-confirm`} className={V3_PRIMARY}>{busy ? "Wird entfernt …" : "Beitrag entfernen"}</button>
        <button type="button" onClick={onKeep} disabled={busy} data-testid={`${testid}-keep`} className={V3_SECONDARY}>Beitrag behalten</button>
      </V3Actions>
    </div>
  );
}

export function CommentItem({ c, postId, onChanged, onRemoved, onReport }) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const perm = contentPermissions(c);
  const base = `/member/comments/${encodeURIComponent(c.id)}`;
  const checkTo = postHref(postId);
  const save = async (body) => {
    const r = await mutate("patch", base, { body }, "Der Kommentar wurde nicht geändert");
    const saved = savedComment(r.data);
    const o = writeOutcome(r, saved && saved.id === c.id ? saved : null);
    if (o.state !== "saved") return o;
    onChanged(saved); setEditing(false); return null;
  };
  const remove = async () => {
    const r = await mutate("delete", base, undefined, "Der Kommentar wurde nicht entfernt");
    const o = writeOutcome(r, removed(r.data), UNCLEAR_REMOVE_TEXT);
    if (o.state !== "saved") return o;
    onRemoved(c.id); return null;
  };
  return (
    <li className="rounded-[10px] border border-v3-line bg-white px-[16px] pb-[14px] pt-[11px]" data-testid={`comment-${c.id}`}>
      <p className="flex flex-wrap gap-x-2 text-[14px] font-bold text-v3-muted"><span>{c.isMine ? `${c.author.name} (du)` : c.author.name}</span>
        {c.createdAt && <span className="font-normal">{formatDateTime(c.createdAt)}</span>}{c.editedAt && <span className="font-normal">· bearbeitet</span>}</p>
      {editing ? <div className="mt-[12px]"><EditBox id={`comment-${c.id}`} label="Kommentar bearbeiten" max={1000} initial={c.body} onSave={save} onCancel={() => setEditing(false)} checkTo={checkTo} /></div>
        : c.status === "visible" ? <p className="mt-[8px] whitespace-pre-wrap break-words text-[15px] leading-[22px] text-v3-ink lg:text-[16px]" data-testid={`comment-${c.id}-body`}>{c.body}</p>
        : <p className="mt-[8px] text-[14px] text-v3-amber" data-testid={`comment-${c.id}-hidden`}>Dieser Kommentar ist ausgeblendet.</p>}
      {!editing && (perm.edit || perm.remove || perm.report) && (
        <div className="mt-[10px] flex flex-wrap gap-2">
          {perm.edit && <button type="button" onClick={() => setEditing(true)} data-testid={`comment-${c.id}-edit-open`} className={V3_SMALL}><Pencil className="h-4 w-4" aria-hidden="true" /> Bearbeiten</button>}
          {perm.remove && <button type="button" onClick={() => setRemoving(true)} data-testid={`comment-${c.id}-remove`} className={V3_SMALL}><Trash2 className="h-4 w-4" aria-hidden="true" /> Entfernen</button>}
          {perm.report && <button type="button" onClick={() => onReport(c)} data-testid={`comment-${c.id}-report`} className={V3_GHOST}><Flag className="h-4 w-4" aria-hidden="true" /> Melden</button>}
        </div>
      )}
      <RemoveDialog open={removing} onOpenChange={setRemoving} testid={`comment-${c.id}-remove-dialog`} title="Kommentar entfernen?" onRemove={remove} checkTo={checkTo}
        impact="Dein Kommentar ist danach für niemanden mehr sichtbar. Der Beitrag selbst bleibt bestehen." />
    </li>
  );
}
